import React from 'react';
import { AlertIcon } from '@eventbrite/marmalade';
import { gettext } from '../i18n';

/**
 * Local error boundary (Valkyrie port).
 *
 * eb-ui used `@eventbrite/error-boundary`, which is DROPPED in Valkyrie: it was
 * a Sentry-backed boundary (`@sentry/react@7` hard-blocks React 19) and Sentry
 * Cloud is decommissioned (see memory.md C1 drop-recipes). We reimplement a tiny
 * Sentry-free class boundary that renders the same fallback the eb-ui widget
 * showed.
 */

interface ErrorBoundaryProps {
    fallback: React.ReactNode;
    children?: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

class ErrorBoundary extends React.Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error): void {
        // eslint-disable-next-line no-console
        console.error('ContactOrganizer error boundary caught:', error);
    }

    render(): React.ReactNode {
        if (this.state.hasError) {
            return this.props.fallback;
        }

        return this.props.children;
    }
}

const ViewFallBack: React.FunctionComponent = () => (
    <div
        className="eds-text--center eds-l-pad-top-6"
        style={{ flexDirection: 'column' }}
    >
        <AlertIcon size="medium" />
        <div className="eds-text-bl eds-l-pad-top-6">{gettext('Uh oh.')}</div>
        <div className="eds-l-pad-top-3">
            {gettext('Looks like we have a problem.')}
        </div>
        <div className="eds-l-pad-top-1">
            {gettext('Our team has been notified and we are investigating.')}
        </div>
    </div>
);

export const ViewErrorBoundary: React.FunctionComponent<{
    children?: React.ReactNode;
}> = (props) => (
    <ErrorBoundary fallback={<ViewFallBack />}>{props.children}</ErrorBoundary>
);

export const ModalErrorBoundary: React.FunctionComponent<{
    children?: React.ReactNode;
}> = (props) => <ErrorBoundary fallback={<div />}>{props.children}</ErrorBoundary>;
